import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, CheckCircle, XCircle, Clock, AlertCircle, 
  Plus, FileText, TrendingUp, User, Filter, RefreshCw
} from 'lucide-react';
import './LeaveManagementTab.css';

const RAW_API_URL = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = RAW_API_URL.replace(/\/+$/, '').endsWith('/api')
  ? RAW_API_URL.replace(/\/+$/, '')
  : `${RAW_API_URL.replace(/\/+$/, '')}/api`;

const LeaveManagementTab = ({ hotelId, token, user }) => {
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState('applications'); // applications, balance, pending, calendar
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [leaveCalendar, setLeaveCalendar] = useState([]);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  
  const [applyFormData, setApplyFormData] = useState({
    staff_profile_id: null,
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    reason: '',
    emergency_contact: ''
  });

  const auth = token ? { headers: { Authorization: `Bearer ${token}` } } : null;

  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave', icon: '🏖️', color: '#3b82f6' },
    { value: 'sick', label: 'Sick Leave', icon: '🏥', color: '#ef4444' },
    { value: 'annual', label: 'Annual Leave', icon: '✈️', color: '#10b981' },
    { value: 'maternity', label: 'Maternity Leave', icon: '👶', color: '#ec4899' },
    { value: 'unpaid', label: 'Unpaid Leave', icon: '⏸️', color: '#6b7280' }
  ];

  useEffect(() => {
    if (token && hotelId) {
      fetchDepartments();
      fetchStaffList();
      const staffProfileId = Number(user?.staff_profile_id);
      if (Number.isFinite(staffProfileId)) {
        fetchLeaveBalance(staffProfileId);
        fetchLeaveApplications(staffProfileId);
      }
      fetchPendingApprovals();
    }
  }, [token, hotelId, user]);

  const fetchDepartments = async () => {
    try {
      const res = await axios.get(`${API_URL}/departments`, auth);
      if (res.data.success) {
        setDepartments(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const res = await axios.get(`${API_URL}/manager/staff`, auth);
      if (res.data.success) {
        const staffData = Array.isArray(res.data.data)
          ? res.data.data
          : (res.data.data?.staff || []);
        setStaffList(staffData);
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
    }
  };

  const fetchLeaveBalance = async (staffProfileId) => {
    try {
      const res = await axios.get(
        `${API_URL}/leave/staff/${staffProfileId}/balance`,
        auth
      );
      if (res.data.success) {
        setLeaveBalance(res.data.data);
      }
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const fetchLeaveApplications = async (staffProfileId) => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_URL}/leave/staff/${staffProfileId}/applications`,
        auth
      );
      if (res.data.success) {
        setLeaveApplications(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching leave applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const params = selectedDepartment ? `?department_id=${selectedDepartment}` : '';
      const res = await axios.get(
        `${API_URL}/leave/hotel/${hotelId}/pending${params}`,
        auth
      );
      if (res.data.success) {
        setPendingApprovals(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const fetchLeaveCalendar = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
      
      const params = new URLSearchParams({ start_date: startDate, end_date: endDate });
      if (selectedDepartment) {
        params.append('department_id', selectedDepartment);
      }

      const res = await axios.get(
        `${API_URL}/leave/hotel/${hotelId}/calendar?${params}`,
        auth
      );
      if (res.data.success) {
        setLeaveCalendar(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching leave calendar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyLeave = async (e) => {
    e.preventDefault();
    if (!auth) return;

    try {
      setLoading(true);
      const res = await axios.post(
        `${API_URL}/leave/apply`,
        applyFormData,
        auth
      );

      if (res.data.success) {
        alert('Leave application submitted successfully!');
        setShowApplyModal(false);
        resetApplyForm();
        if (user && user.staff_profile_id) {
          fetchLeaveApplications(user.staff_profile_id);
          fetchLeaveBalance(user.staff_profile_id);
        }
        fetchPendingApprovals();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error applying for leave');
    } finally {
      setLoading(false);
    }
  };

  const handleProcessLeave = async (applicationId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this leave application?`)) {
      return;
    }

    try {
      setLoading(true);
      const res = await axios.put(
        `${API_URL}/leave/applications/${applicationId}/process`,
        {
          action: action,
          reviewer_comments: `${action === 'approve' ? 'Approved' : 'Rejected'} by manager`
        },
        auth
      );

      if (res.data.success) {
        alert(`Leave application ${action}d successfully!`);
        fetchPendingApprovals();
        if (user && user.staff_profile_id) {
          fetchLeaveApplications(user.staff_profile_id);
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || `Error ${action}ing leave`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelLeave = async (applicationId) => {
    const reason = window.prompt('Please provide a reason for cancellation:');
    if (!reason) return;

    try {
      setLoading(true);
      const res = await axios.put(
        `${API_URL}/leave/applications/${applicationId}/cancel`,
        { cancellation_reason: reason },
        auth
      );

      if (res.data.success) {
        alert('Leave application cancelled successfully!');
        if (user && user.staff_profile_id) {
          fetchLeaveApplications(user.staff_profile_id);
          fetchLeaveBalance(user.staff_profile_id);
        }
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Error cancelling leave');
    } finally {
      setLoading(false);
    }
  };

  const resetApplyForm = () => {
    setApplyFormData({
      staff_profile_id: null,
      leave_type: 'casual',
      start_date: '',
      end_date: '',
      reason: '',
      emergency_contact: ''
    });
  };

  const getLeaveTypeInfo = (type) => {
    return leaveTypes.find(lt => lt.value === type) || leaveTypes[0];
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'pending': 'status-pending',
      'approved': 'status-approved',
      'rejected': 'status-rejected',
      'cancelled': 'status-cancelled'
    };
    return classes[status] || '';
  };

  useEffect(() => {
    if (activeView === 'calendar') {
      fetchLeaveCalendar();
    } else if (activeView === 'pending') {
      fetchPendingApprovals();
    }
  }, [activeView, selectedDepartment]);

  return (
    <div className="leave-management-tab">
      {/* View Switcher */}
      <div className="view-switcher">
        <button
          className={activeView === 'applications' ? 'active' : ''}
          onClick={() => setActiveView('applications')}
        >
          <FileText size={18} />
          My Applications
        </button>
        <button
          className={activeView === 'balance' ? 'active' : ''}
          onClick={() => setActiveView('balance')}
        >
          <TrendingUp size={18} />
          Leave Balance
        </button>
        <button
          className={activeView === 'pending' ? 'active' : ''}
          onClick={() => setActiveView('pending')}
        >
          <AlertCircle size={18} />
          Pending Approvals
          {pendingApprovals.length > 0 && (
            <span className="badge">{pendingApprovals.length}</span>
          )}
        </button>
        <button
          className={activeView === 'calendar' ? 'active' : ''}
          onClick={() => setActiveView('calendar')}
        >
          <Calendar size={18} />
          Leave Calendar
        </button>
      </div>

      {/* Leave Balance View */}
      {activeView === 'balance' && (
        <div className="content-card">
          <div className="card-header">
            <h2>
              <TrendingUp size={24} />
              Leave Balance
            </h2>
            <button className="btn-primary" onClick={() => setShowApplyModal(true)}>
              <Plus size={18} />
              Apply for Leave
            </button>
          </div>

          {leaveBalance ? (
            <div className="balance-grid">
              {Object.entries(leaveBalance).map(([type, balance]) => {
                const typeInfo = getLeaveTypeInfo(type);
                return (
                  <div key={type} className="balance-card" style={{ borderLeftColor: typeInfo.color }}>
                    <div className="balance-icon" style={{ background: `${typeInfo.color}20` }}>
                      <span style={{ fontSize: '2rem' }}>{typeInfo.icon}</span>
                    </div>
                    <div className="balance-info">
                      <h3>{typeInfo.label}</h3>
                      <div className="balance-stats">
                        <div className="stat">
                          <label>Total</label>
                          <value>{balance.total || 0}</value>
                        </div>
                        <div className="stat">
                          <label>Used</label>
                          <value className="used">{balance.used || 0}</value>
                        </div>
                        <div className="stat">
                          <label>Available</label>
                          <value className="available">{balance.available || 0}</value>
                        </div>
                      </div>
                      <div className="balance-bar">
                        <div 
                          className="balance-fill" 
                          style={{ 
                            width: `${((balance.used || 0) / (balance.total || 1)) * 100}%`,
                            background: typeInfo.color
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <TrendingUp size={48} />
              <p>No leave balance data available</p>
            </div>
          )}
        </div>
      )}

      {/* My Applications View */}
      {activeView === 'applications' && (
        <div className="content-card">
          <div className="card-header">
            <h2>
              <FileText size={24} />
              My Leave Applications
            </h2>
            <button className="btn-primary" onClick={() => setShowApplyModal(true)}>
              <Plus size={18} />
              Apply for Leave
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading applications...</p>
            </div>
          ) : leaveApplications.length > 0 ? (
            <div className="applications-list">
              {leaveApplications.map(app => {
                const typeInfo = getLeaveTypeInfo(app.leave_type);
                return (
                  <div key={app.application_id} className="application-card">
                    <div className="app-header">
                      <div className="app-type" style={{ background: `${typeInfo.color}20`, color: typeInfo.color }}>
                        {typeInfo.icon} {typeInfo.label}
                      </div>
                      <span className={`status-badge ${getStatusBadgeClass(app.status)}`}>
                        {app.status}
                      </span>
                    </div>
                    <div className="app-dates">
                      <div>
                        <label>From:</label>
                        <span>{new Date(app.start_date).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <label>To:</label>
                        <span>{new Date(app.end_date).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <label>Days:</label>
                        <span className="days-badge">{app.number_of_days}</span>
                      </div>
                    </div>
                    <div className="app-reason">
                      <label>Reason:</label>
                      <p>{app.reason}</p>
                    </div>
                    {app.reviewer_comments && (
                      <div className="app-comments">
                        <label>Manager Comments:</label>
                        <p>{app.reviewer_comments}</p>
                      </div>
                    )}
                    {app.status === 'pending' && (
                      <div className="app-actions">
                        <button 
                          className="btn-danger" 
                          onClick={() => handleCancelLeave(app.application_id)}
                        >
                          Cancel Application
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <FileText size={48} />
              <p>No leave applications found</p>
              <button className="btn-primary" onClick={() => setShowApplyModal(true)}>
                <Plus size={18} />
                Apply for Leave
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pending Approvals View (Manager) */}
      {activeView === 'pending' && (
        <div className="content-card">
          <div className="card-header">
            <h2>
              <AlertCircle size={24} />
              Pending Approvals
            </h2>
            <div className="card-actions">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="filter-select"
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>
                    {dept.department_name}
                  </option>
                ))}
              </select>
              <button className="btn-icon" onClick={fetchPendingApprovals}>
                <RefreshCw size={18} />
              </button>
            </div>
          </div>

          {pendingApprovals.length > 0 ? (
            <div className="applications-list">
              {pendingApprovals.map(app => {
                const typeInfo = getLeaveTypeInfo(app.leave_type);
                return (
                  <div key={app.application_id} className="application-card pending">
                    <div className="app-header">
                      <div className="app-staff">
                        <User size={20} />
                        <div>
                          <h4>{app.full_name}</h4>
                          <span>{app.department_name}</span>
                        </div>
                      </div>
                      <div className="app-type" style={{ background: `${typeInfo.color}20`, color: typeInfo.color }}>
                        {typeInfo.icon} {typeInfo.label}
                      </div>
                    </div>
                    <div className="app-dates">
                      <div>
                        <label>From:</label>
                        <span>{new Date(app.start_date).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <label>To:</label>
                        <span>{new Date(app.end_date).toLocaleDateString()}</span>
                      </div>
                      <div>
                        <label>Days:</label>
                        <span className="days-badge">{app.number_of_days}</span>
                      </div>
                    </div>
                    <div className="app-reason">
                      <label>Reason:</label>
                      <p>{app.reason}</p>
                    </div>
                    {app.emergency_contact && (
                      <div className="app-contact">
                        <label>Emergency Contact:</label>
                        <span>{app.emergency_contact}</span>
                      </div>
                    )}
                    <div className="app-actions">
                      <button 
                        className="btn-primary"
                        onClick={() => handleProcessLeave(app.application_id, 'approve')}
                        disabled={loading}
                      >
                        <CheckCircle size={18} />
                        Approve
                      </button>
                      <button 
                        className="btn-danger"
                        onClick={() => handleProcessLeave(app.application_id, 'reject')}
                        disabled={loading}
                      >
                        <XCircle size={18} />
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <CheckCircle size={48} style={{ color: '#10b981' }} />
              <p>No pending leave applications</p>
            </div>
          )}
        </div>
      )}

      {/* Leave Calendar View */}
      {activeView === 'calendar' && (
        <div className="content-card">
          <div className="card-header">
            <h2>
              <Calendar size={24} />
              Leave Calendar
            </h2>
            <select
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              className="filter-select"
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.department_id} value={dept.department_id}>
                  {dept.department_name}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading calendar...</p>
            </div>
          ) : leaveCalendar.length > 0 ? (
            <div className="calendar-list">
              {leaveCalendar.map((leave, index) => {
                const typeInfo = getLeaveTypeInfo(leave.leave_type);
                return (
                  <div key={index} className="calendar-item">
                    <div className="calendar-date" style={{ background: typeInfo.color }}>
                      <span className="month">{new Date(leave.start_date).toLocaleDateString('en', { month: 'short' })}</span>
                      <span className="day">{new Date(leave.start_date).getDate()}</span>
                    </div>
                    <div className="calendar-info">
                      <h4>{leave.full_name}</h4>
                      <p>{typeInfo.label} • {leave.number_of_days} day(s)</p>
                      <span className="calendar-range">
                        {new Date(leave.start_date).toLocaleDateString()} - {new Date(leave.end_date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <Calendar size={48} />
              <p>No leaves scheduled for this month</p>
            </div>
          )}
        </div>
      )}

      {/* Apply Leave Modal */}
      {showApplyModal && (
        <div className="modal-overlay" onClick={() => setShowApplyModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Apply for Leave</h2>
              <button className="modal-close" onClick={() => setShowApplyModal(false)}>×</button>
            </div>
            <form onSubmit={handleApplyLeave}>
              <div className="form-group">
                <label>Staff Member *</label>
                <select
                  value={applyFormData.staff_profile_id || ''}
                  onChange={(e) => {
                    const staffId = parseInt(e.target.value);
                    setApplyFormData({ ...applyFormData, staff_profile_id: staffId });
                    fetchLeaveBalance(staffId);
                  }}
                  required
                >
                  <option value="">Select staff member</option>
                  {staffList.map(staff => (
                    <option key={staff.staff_profile_id} value={staff.staff_profile_id}>
                      {staff.name} - {staff.role}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Leave Type *</label>
                <div className="leave-type-selector">
                  {leaveTypes.map(type => (
                    <label key={type.value} className="leave-type-option">
                      <input
                        type="radio"
                        name="leave_type"
                        value={type.value}
                        checked={applyFormData.leave_type === type.value}
                        onChange={(e) => setApplyFormData({ ...applyFormData, leave_type: e.target.value })}
                      />
                      <span className="type-card" style={{ borderColor: type.color }}>
                        <span className="type-icon">{type.icon}</span>
                        <span className="type-label">{type.label}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    value={applyFormData.start_date}
                    onChange={(e) => setApplyFormData({ ...applyFormData, start_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    value={applyFormData.end_date}
                    onChange={(e) => setApplyFormData({ ...applyFormData, end_date: e.target.value })}
                    min={applyFormData.start_date || new Date().toISOString().split('T')[0]}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  value={applyFormData.reason}
                  onChange={(e) => setApplyFormData({ ...applyFormData, reason: e.target.value })}
                  placeholder="Please provide a reason for your leave..."
                  rows="3"
                  required
                />
              </div>

              <div className="form-group">
                <label>Emergency Contact (Optional)</label>
                <input
                  type="tel"
                  value={applyFormData.emergency_contact}
                  onChange={(e) => setApplyFormData({ ...applyFormData, emergency_contact: e.target.value })}
                  placeholder="+1234567890"
                />
              </div>

              {leaveBalance && applyFormData.leave_type && (
                <div className="balance-preview">
                  <h4>Current Balance: {leaveTypes.find(t => t.value === applyFormData.leave_type)?.label}</h4>
                  <div className="balance-summary">
                    <span>Available: <strong>{leaveBalance[applyFormData.leave_type]?.available || 0}</strong></span>
                    <span>Used: <strong>{leaveBalance[applyFormData.leave_type]?.used || 0}</strong></span>
                    <span>Total: <strong>{leaveBalance[applyFormData.leave_type]?.total || 0}</strong></span>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowApplyModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagementTab;